let cached: Promise<Set<string>> | null = null;

/** Load the bundled English word list (~6500 common words). Cached after first fetch. */
export function loadDictionary(): Promise<Set<string>> {
  if (!cached) {
    cached = fetch(`${import.meta.env.BASE_URL}valid_words.txt`)
      .then((r) => r.text())
      .then((text) => {
        const set = new Set<string>();
        for (const line of text.split('\n')) {
          const trimmed = line.trim().toUpperCase();
          if (trimmed) set.add(trimmed);
        }
        return set;
      })
      .catch((e) => {
        console.error('[dictionary] failed to load', e);
        return new Set<string>();
      });
  }
  return cached;
}

export async function isValidWord(word: string): Promise<boolean> {
  const set = await loadDictionary();
  return set.has(word.toUpperCase());
}

let cachedFull: Promise<Set<string>> | null = null;

/**
 * The full 125k SCOWL wordlist shipped by the iOS app (~309 KB gzipped).
 *
 * The Quest Gauntlet games need real dictionary coverage, not the ~6.5k common
 * list: Pivot's par solutions walk through words like RELICS and SPICED, Cipher
 * rejects any source word it doesn't recognise, and Blackbox resolves probes
 * against it. With the small list those puzzles are unplayable — Pivot is
 * literally unwinnable.
 *
 * Loaded lazily and cached, so only players who open the gauntlet pay for it.
 */
/**
 * Word-acceptance check against the full list. This is what every iOS game uses
 * (`WordDictionary.shared.isValidWord`), so the small common list would wrongly
 * reject perfectly valid entries — bonus words in Word Circle, chain words in
 * Word Dominoes, and so on.
 */
export async function isValidWordFull(word: string): Promise<boolean> {
  const set = await loadFullDictionary();
  return set.has(word.toUpperCase());
}

export function loadFullDictionary(): Promise<Set<string>> {
  if (!cachedFull) {
    cachedFull = fetch(`${import.meta.env.BASE_URL}full_words.txt`)
      .then((r) => r.text())
      .then((text) => {
        const set = new Set<string>();
        for (const line of text.split('\n')) {
          const trimmed = line.trim().toUpperCase();
          if (trimmed) set.add(trimmed);
        }
        return set;
      })
      .catch((e) => {
        console.error('[dictionary] failed to load full list', e);
        return new Set<string>();
      });
  }
  return cachedFull;
}
